import jsonata from 'jsonata';
import { debounce } from 'lodash';
import type * as monaco from 'monaco-editor';
import dynamic from 'next/dynamic';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';

const MonacoEditor = dynamic(() => import('@monaco-editor/react'), { ssr: false });

function setupJSONataLanguage(monaco: any) {
  monaco.languages.register({ id: 'jsonata' });

  const keywords = [
    'and',
    'or',
    'not',
    'in',
    'if',
    'then',
    'else',
    'true',
    'false',
    'null',
    'function',
  ];

  const builtins = [
    '$length',
    '$substring',
    '$uppercase',
    '$lowercase',
    '$number',
    '$string',
    '$boolean',
    '$sum',
    '$count',
    '$map',
    '$reduce',
    '$each',
    '$exists',
    '$not',
    '$sort',
    '$filter',
    '$contains',
    '$join',
    '$distinct',
  ];

  monaco.languages.setLanguageConfiguration('jsonata', {
    brackets: [
      ['{', '}'],
      ['[', ']'],
      ['(', ')'],
    ],
  });

  // Tokenization using Monarch
  monaco.languages.setMonarchTokensProvider('jsonata', {
    keywords,
    builtins,

    brackets: [
      ['{', '}', 'delimiter.bracket'],
      ['[', ']', 'delimiter.bracket'],
      ['(', ')', 'delimiter.parenthesis'],
    ],

    tokenizer: {
      root: [
        // Single-line comment
        [/\/\/.*$/, 'comment'],
        // Multi-line comment
        [/\/\*[\s\S]*?\*\//, 'comment'],

        // Whitespace
        { include: '@whitespace' },

        // Operators
        // e.g. ->, ~>, ..
        [/~>/, 'operator.pipeline'],
        [/->/, 'operator.partial'],
        [/\.\./, 'operator.recursive'],
        [/\./, 'operator.dot'],

        // Delimiters and punctuation
        [/[,:;]/, 'delimiter'],

        // Operators
        [/[=!<>]=?/, 'operator'], // =, ==, !=, <=, >=
        [/[+\-*/%&|^~]/, 'operator'], // +, -, *, /, %, &, |, ^
        [/\?/, 'operator'],

        // Numbers (integer or float). You can refine these patterns if needed.
        [/\d+(\.\d+)?([eE][-+]?\d+)?/, 'number'],

        // Strings
        [/"([^"\\]|\\.)*$/, 'string.invalid'], // non-terminated string
        [/'([^'\\]|\\.)*$/, 'string.invalid'], // non-terminated string
        [/"/, { token: 'string.quote', bracket: '@open', next: '@stringDouble' }],
        [/'/, { token: 'string.quote', bracket: '@open', next: '@stringSingle' }],

        // Variable references e.g. $foo or just $
        [
          /\$[a-zA-Z_]\w*/,
          {
            cases: {
              '@builtins': 'predefined', // Check builtins first
              '@default': 'variable', // If not a builtin, then it's a variable
            },
          },
        ],
        [/\$/, 'variable'],

        // Identifiers or keywords or builtins
        [
          /[a-zA-Z_]\w*/,
          {
            cases: {
              '@keywords': 'keyword',
              '@builtins': 'predefined',
              // Detect function calls
              '\\w+\\s*\\(': 'function',
              '@default': 'identifier',
            },
          },
        ],
      ],

      // Whitespace handling
      whitespace: [[/\s+/, 'white']],

      // Double-quoted string
      stringDouble: [
        [/[^\\"]+/, 'string'],
        [/\\./, 'string.escape'], // Escaped character
        [/"/, { token: 'string.quote', bracket: '@close', next: '@pop' }],
      ],

      // Single-quoted string
      stringSingle: [
        [/[^\\']+/, 'string'],
        [/\\./, 'string.escape'],
        [/'/, { token: 'string.quote', bracket: '@close', next: '@pop' }],
      ],
    },
  });

  monaco.editor.defineTheme('jsonata-theme', {
    base: 'vs',
    inherit: true,
    rules: [
      // Comments
      { token: 'comment', foreground: '30972f' },
      // Keywords (if, then, else, in, and, or, etc.)
      { token: 'keyword', foreground: '372cc9' },
      // Built-in JSONata functions (e.g. $length, $map)
      { token: 'predefined', foreground: 'ff4f14' },
      // Functions
      { token: 'function', foreground: '372cc9' },
      // Variables ($foo, $)
      { token: 'variable', foreground: '267F99' },
      // Operators
      { token: 'operator', foreground: '0000FF' },
      { token: 'operator.dot', foreground: '0000FF' },
      { token: 'operator.recursive', foreground: '7B1FA2', fontStyle: 'bold' },
      { token: 'operator.pipeline', foreground: '7B1FA2', fontStyle: 'bold' },
      { token: 'operator.partial', foreground: '7B1FA2', fontStyle: 'bold' },
      // Delimiters
      { token: 'delimiter', foreground: '000000' },
      // Numbers
      { token: 'number', foreground: '09885A' },
      // Strings
      { token: 'string', foreground: 'B22222' },
      { token: 'string.escape', foreground: 'D16969', fontStyle: 'bold' },
      // Identifiers
      { token: 'identifier', foreground: '333333' },
    ],
    colors: {
      'editorError.foreground': '#ff0000',
      'editorError.border': '#ff0000',
      'editorError.background': '#ff000010',
      'errorLens.errorForeground': '#ff0000',
      'errorLens.errorBackground': '#ff000010',
    },
  });

  function validateJSONata(
    value: string,
    model: monaco.editor.ITextModel,
    onErrorChange?: (hasError: boolean) => void,
  ) {
    if (!value?.trim()) {
      onErrorChange?.(false);
      return;
    }

    try {
      jsonata(value);
      monaco.editor.setModelMarkers(model, 'jsonata', []);
      onErrorChange?.(false);
    } catch (error: any) {
      const errorMsg = error.message;
      let positionNumber: number | undefined = error.position;

      if (typeof positionNumber !== 'number') {
        const posMatch = errorMsg.match(/at position (\d+)/i);
        if (posMatch) {
          positionNumber = parseInt(posMatch[1], 10);
        }
      }

      let startLine = 1,
        startColumn = 1,
        endLine = 1,
        endColumn = 1;

      if (typeof positionNumber === 'number') {
        const startPos = model.getPositionAt(positionNumber);

        const wordAtPosition = model.getWordAtPosition(startPos);
        let tokenLength = 1;
        if (wordAtPosition) {
          tokenLength = wordAtPosition.endColumn - wordAtPosition.startColumn;
        } else {
          const tokenMatch = value.substring(positionNumber).match(/^\S+/);
          tokenLength = tokenMatch ? tokenMatch[0].length : 1;
        }

        const endPos = model.getPositionAt(positionNumber + tokenLength);

        startLine = startPos.lineNumber;
        startColumn = startPos.column;
        endLine = endPos.lineNumber;
        endColumn = endPos.column;
      } else {
        const lines = value.split('\n');
        startLine = 1;
        startColumn = 1;
        endLine = lines.length;
        endColumn = lines[lines.length - 1].length + 1;
      }

      const marker = {
        severity: monaco.MarkerSeverity.Error,
        message: errorMsg,
        startLineNumber: startLine,
        startColumn,
        endLineNumber: endLine,
        endColumn,
        source: 'JSONata',
        code: 'syntax_error',
      };

      monaco.editor.setModelMarkers(model, 'jsonata', [marker]);
      onErrorChange?.(true);
    }
  }

  return validateJSONata;
}

interface JSONataEditorProps {
  value: string;
  onChange: (value: string) => void;
  readOnly?: boolean;
  options?: monaco.editor.IStandaloneEditorConstructionOptions;
  onErrorChange?: (hasError: boolean) => void;
}

const JSONataEditor = React.memo(function JSONataEditor({
  value,
  onChange,
  readOnly = false,
  options = {},
  onErrorChange,
}: JSONataEditorProps) {
  const validateRef = useRef<
    | ((
        value: string,
        model: monaco.editor.ITextModel,
        onErrorChange?: (hasError: boolean) => void,
      ) => void)
    | null
  >(null);

  const editorOptions = useMemo<monaco.editor.IStandaloneEditorConstructionOptions>(
    () => ({
      automaticLayout: true,
      autoClosingBrackets: 'always',
      autoClosingQuotes: 'always',
      formatOnPaste: true,
      formatOnType: true,
      scrollBeyondLastLine: false,
      tabSize: 2,
      minimap: { enabled: false },
      lineNumbers: 'on',
      wordWrap: 'off',
      readOnly,
      ...options,
    }),
    [readOnly, options],
  );

  const debouncedValidate = useMemo(
    () =>
      debounce((editor: monaco.editor.IStandaloneCodeEditor, model: monaco.editor.ITextModel) => {
        if (validateRef.current) {
          validateRef.current(editor.getValue(), model, onErrorChange);
        }
      }, 300),
    [onErrorChange],
  );

  const handleChange = useCallback((text: string | undefined) => onChange(text ?? ''), [onChange]);

  const handleEditorWillMount = useCallback((monaco: any) => {
    validateRef.current = setupJSONataLanguage(monaco);
  }, []);

  const handleEditorDidMount = useCallback(
    (editor: monaco.editor.IStandaloneCodeEditor) => {
      const model = editor.getModel();
      if (model && validateRef.current) {
        validateRef.current(value, model, onErrorChange);

        editor.onDidChangeModelContent(() => {
          debouncedValidate(editor, model);
        });
      }
    },
    [value, onErrorChange, debouncedValidate],
  );

  useEffect(() => {
    return () => {
      debouncedValidate.cancel();
    };
  }, [debouncedValidate]);

  return (
    <MonacoEditor
      language="jsonata"
      theme="jsonata-theme"
      beforeMount={handleEditorWillMount}
      onMount={handleEditorDidMount}
      value={value}
      onChange={handleChange}
      options={editorOptions}
    />
  );
});

export default JSONataEditor;
